import { useId, useRef } from 'react'
import type {
  EditableThemeTokenName,
  ThemeVariant,
} from '@/shared/theme/theme-config'
import { cn } from '@/shared/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { tokenValueToHex } from '@/shared/theme/color-utils'
import {
  curatedPaletteFamilies,
  findPaletteSwatch,
} from '@/shared/theme/theme-palette'

function ColorPreview({
  value,
  className,
}: {
  value: string
  className?: string
}) {
  return (
    <span
      className={cn(
        'h-3.5 w-3.5 shrink-0 rounded-sm border border-border/80',
        className,
      )}
      style={{ backgroundColor: value }}
      aria-hidden="true"
    />
  )
}

interface AppSwatch {
  label: string
  value: string
}

function AppSwatches({
  swatches,
  value,
  variant,
  token,
  onChange,
}: {
  swatches: Array<AppSwatch>
  value: string
  variant: ThemeVariant
  token: EditableThemeTokenName
  onChange: (value: string) => void
}) {
  if (swatches.length === 0) {
    return null
  }

  return (
    <>
      <div className="mb-2 px-1 text-xs font-medium text-muted-foreground">
        App colors
      </div>
      <div className="flex flex-wrap gap-2">
        {swatches.map((swatch) => (
          <button
            key={`${variant}-${token}-app-${swatch.label}`}
            type="button"
            onClick={() => onChange(swatch.value)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-accent',
              value === swatch.value && 'border-primary ring-1 ring-primary/40',
            )}
            aria-label={`${swatch.label} ${token}`}
          >
            <ColorPreview value={swatch.value} />
            {swatch.label}
          </button>
        ))}
      </div>
      <div className="my-3 h-px bg-border" />
    </>
  )
}

function PaletteGrid({
  value,
  variant,
  token,
  onChange,
}: {
  value: string
  variant: ThemeVariant
  token: EditableThemeTokenName
  onChange: (value: string) => void
}) {
  return (
    <>
      <div className="mb-2 px-1 text-xs font-medium text-muted-foreground">
        Palette
      </div>

      <div className="max-h-[320px] space-y-3 overflow-y-auto">
        {curatedPaletteFamilies.map((family) => (
          <div key={family.name}>
            <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {family.name}
            </div>
            <div className="flex flex-wrap gap-1">
              {family.swatches.map((swatch) => {
                const isSelected = value === swatch.value

                return (
                  <button
                    key={`${variant}-${token}-${family.name}-${swatch.shade}`}
                    type="button"
                    onClick={() => onChange(swatch.value)}
                    className={cn(
                      'h-5 w-5 rounded-sm border transition-all hover:scale-125 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden',
                      isSelected &&
                        'border-foreground ring-1 ring-foreground/50 scale-110',
                    )}
                    style={{ backgroundColor: swatch.value }}
                    aria-label={`${family.name} ${swatch.shade}`}
                    title={`${family.name} ${swatch.shade}`}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function NativeColorFallback({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const colorId = useId()
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const hexValue = tokenValueToHex(value)

  function handleNativeChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(event.target.value)
  }

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground"
        onClick={() => nativeInputRef.current?.click()}
      >
        <ColorPreview value={value} />
        Custom...
      </button>

      <input
        id={`${colorId}-native-color`}
        ref={nativeInputRef}
        type="color"
        value={hexValue}
        onChange={handleNativeChange}
        className="sr-only"
        tabIndex={-1}
      />
    </>
  )
}

export function ColorPickerDropdown({
  value,
  onChange,
  variant,
  token,
  appSwatches,
}: {
  value: string
  onChange: (value: string) => void
  variant: ThemeVariant
  token: EditableThemeTokenName
  appSwatches: Array<AppSwatch>
}) {
  const exactSwatch = findPaletteSwatch(value)

  // De-duplicate app swatches by value
  const deduplicatedAppSwatches = appSwatches.filter(
    (swatch, index, all) =>
      all.findIndex((item) => item.value === swatch.value) === index,
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-input bg-transparent px-2 py-1.5 text-xs hover:bg-accent',
          )}
          aria-label={`Pick color for ${token}`}
        >
          <ColorPreview value={value} />
          <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[80px]">
            {exactSwatch ? `${exactSwatch.family} ${exactSwatch.shade}` : value}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[360px] p-3"
        align="start"
        sideOffset={4}
      >
        <AppSwatches
          swatches={deduplicatedAppSwatches}
          value={value}
          variant={variant}
          token={token}
          onChange={onChange}
        />

        <PaletteGrid
          value={value}
          variant={variant}
          token={token}
          onChange={onChange}
        />

        <div className="my-3 h-px bg-border" />

        <NativeColorFallback value={value} onChange={onChange} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
